import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { IMAGES, PRODUCT, fmt, COD_FEE } from "../constants/site";
import { ShoppingBag, ArrowRight } from "lucide-react";
import CountdownTimer from "./CountdownTimer";

export default function StickyBuyBar() {
  const { add, setIsOpen } = useCart();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (v) => setVisible(v > 620));

  const buyNow = () => {
    add(1);
    toast.success("OmniMist added — taking you to checkout");
    navigate("/checkout");
  };

  const addToCart = () => {
    add(1);
    setIsOpen(true);
    toast.success("Added to cart — free shipping applied");
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 110 }}
          animate={{ y: 0 }}
          exit={{ y: 110 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-0 bottom-0 z-40"
          data-testid="sticky-buy-bar"
        >
          <div className="border-t border-charcoal-900/10 bg-oat-50/90 backdrop-blur-xl">
            <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:px-10">
              <img
                src={IMAGES.studio}
                alt="OmniMist oil sprayer"
                className="hidden h-12 w-12 rounded-xl object-cover ring-1 ring-charcoal-900/10 sm:block"
              />
              <div className="hidden min-w-0 flex-1 sm:block">
                <p className="truncate font-serif text-base text-charcoal-900 md:text-lg">
                  OmniMist™ 2-in-1 Oil Sprayer &amp; Dispenser
                  <span className="ml-2 hidden text-sm text-charcoal-500 md:inline">470ml</span>
                </p>
                <p className="hidden items-center gap-2 text-xs text-charcoal-500 md:flex">
                  <span className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-3 w-3 fill-gold text-gold" />
                    ))}
                  </span>
                  4.9 · {PRODUCT.reviews} chefs
                </p>
              </div>
              <div className="hidden flex-col items-start gap-1 sm:flex">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-xl font-semibold text-charcoal-900">
                    {fmt(PRODUCT.price + COD_FEE)}
                  </span>
                  <span className="text-sm font-semibold text-charcoal-800 line-through decoration-red-400/80">
                    {fmt(PRODUCT.compareAt)}
                  </span>
                </div>
                <CountdownTimer compact />
              </div>
              <div className="flex flex-1 items-center justify-end gap-2.5 sm:flex-none">
                <button
                  onClick={addToCart}
                  data-testid="sticky-add-to-cart-btn"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-charcoal-900 bg-oat-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-900 transition-all duration-300 hover:border-sage-600 hover:bg-sage-100 active:scale-95 sm:flex-none sm:px-5"
                >
                  <ShoppingBag className="h-3.5 w-3.5" /> Add to Cart
                </button>
                <button
                  onClick={buyNow}
                  data-testid="sticky-buy-btn"
                  className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-charcoal-900 to-sage-800 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-oat-100 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-12px_rgba(124,152,133,0.8)] active:scale-95 sm:flex-none sm:px-6"
                >
                  Buy Now
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
